import { useEffect, useRef, useState } from "react";
import { subscribeToResize } from "./resize-observer";
import { IPagerApi } from "../types/pager-api";
import { VIEWPORT_BREAKPOINT_768 } from "../constants";

export function usePager(initialViewPortWidth: number, productCount: number): IPagerApi {
  const VIEWPORT_769_AND_LARGER_PAGESIZE: number = 4;
  const VIEWPORT_768_AND_SMALLER_PAGESIZE: number = 2;
  const productCountRef = useRef(productCount);

  const [viewPortWidth, setViewPortWidth] = useState(initialViewPortWidth);
  const [previousViewPortWidth, setPreviousViewPortWidth] = useState(initialViewPortWidth);
  const [pageCount, setPageCount] = useState(1);
  const [pageSize, setPageSize] = useState(VIEWPORT_769_AND_LARGER_PAGESIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [placeholderCount, setPlaceholderCount] = useState(0);

  useEffect(() => {
    const currentPageSize =
      viewPortWidth > VIEWPORT_BREAKPOINT_768
        ? VIEWPORT_769_AND_LARGER_PAGESIZE
        : VIEWPORT_768_AND_SMALLER_PAGESIZE;

    if (pageSize === currentPageSize) return;
    setPageSize(currentPageSize);
    setCurrentPage(1);
  }, [viewPortWidth, pageSize]);

  useEffect(() => {
    const currentPageCount = Math.ceil(productCountRef.current / pageSize);
    setPageCount(currentPageCount);

    if (pageSize === VIEWPORT_768_AND_SMALLER_PAGESIZE) {
      const additionalTiles = productCountRef.current % VIEWPORT_768_AND_SMALLER_PAGESIZE;
      setPlaceholderCount(additionalTiles);
    } else if (pageSize === VIEWPORT_769_AND_LARGER_PAGESIZE) {
      const additionalTiles = productCountRef.current % VIEWPORT_769_AND_LARGER_PAGESIZE;
      setPlaceholderCount(
        additionalTiles > 0 ? VIEWPORT_769_AND_LARGER_PAGESIZE - additionalTiles : 0
      );
    }
  }, [pageSize]);

  useEffect(() => {
    // reset scroll position when viewport is expanded
    const threshold = -100;
    const widthDiff = previousViewPortWidth - viewPortWidth;
    if (widthDiff <= threshold) setCurrentPage(1);
  }, [viewPortWidth, previousViewPortWidth]);

  useEffect(() => {
    const unsubscribe = subscribeToResize((width) => {
      setPreviousViewPortWidth(viewPortWidth);
      setViewPortWidth(width);
    });

    return unsubscribe;
  });

  const goToNextPage = () => {
    if (currentPage < pageCount) setCurrentPage(currentPage + 1);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  return {
    pageSize,
    pageCount,
    currentPage,
    placeholderCount,
    goToNextPage,
    goToPreviousPage
  };
}
